import React, { useState, useEffect } from "react";
import { Box, Typography, Card, Container, useTheme, Snackbar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { addActiveRequests, updateLoading, updateLoadingMsg } from "../../Redux/Reducers/uiSlice";
import { selectSecurities, selectSecuritiesLoaded, selectActiveRequests } from "../../Redux/Reducers/selectors";
import SecurityMasterTable from "./SecurityMasterTable";
import SecurityMasterDialog from "./SecurityMasterDialog";
import { Helmet } from "react-helmet";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";

const SecurityMaster = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const securities = useSelector(selectSecurities);
  const securitiesLoaded = useSelector(selectSecuritiesLoaded);
  const activeRequests = useSelector(selectActiveRequests); 
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSecurity, setEditSecurity] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    // Only fetch securities data if it hasn't been loaded before
    if (!securitiesLoaded) {
      const fetchReq = {
        action: "get_security_master",
      };
      sendWebSocketMessage(JSON.stringify(fetchReq));
      dispatch(addActiveRequests("get_security_master"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Loading securities..."));
    }
  }, [dispatch, securitiesLoaded]);

  const handleAdd = () => {
    setEditSecurity(null);
    setDialogOpen(true);
  };

  const handleEdit = (security) => {
    setEditSecurity(security);
    setDialogOpen(true);
  };

  const handleDelete = (security) => {
    // Implement delete functionality
    setSnackbar({ open: true, message: "Security deleted!" });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditSecurity(null);
  };

  const handleSave = (security) => {
    if (!security || Object.keys(security).length === 0) return;
    
    // Implement save functionality
    if (security.id) {
      setSnackbar({ open: true, message: "Security updated!" });
    } else {
      setSnackbar({ open: true, message: "Security added!" });
    }
    setDialogOpen(false);
    setEditSecurity(null);
  };

  const isLoading = activeRequests.includes("get_security_master");

  return (
    <>
      <Helmet>
        <title>Security Master | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Security Master
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Manage security master data and ticker mappings
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <SecurityMasterTable
              securities={securities}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={handleAdd}
              isLoading={isLoading}
            />
          </Card>
        </Container>
        
        <SecurityMasterDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          security={editSecurity}
          onSave={handleSave}
        />
        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Box>
    </>
  );
};

export default SecurityMaster; 